import { useMemo } from 'react'
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Stack, Typography, useTheme } from '@mui/material'

import HeaderBar, { Breadcrumbs, Links } from '../components/HeaderBar'
import { MENU_PROJECTS } from '../../Sidebar'
import { useContentProjects } from './store'
import { CATEGORIES_PROJECT, STATUS } from 'pages/Projects/constants'

const Home = () => {
  const { t } = useTranslation()
  return (
    <Breadcrumbs>
      <Links
        links={[{ active: true, onClick: () => { }, label: t('navigation.projects.title') }]}
        onClick={() => { }}
      />
    </Breadcrumbs>
  )
}

const Detail = () => {
  const { t } = useTranslation()
  const [{ selectedTab, project }, setProjects] = useContentProjects()

  const theme = useTheme()
  const tabs = [
    {
      key: 'home',
      label: t('project.tabs.detail'),
      active: selectedTab === 'home',
      onClick: () => {
        setProjects(prev => ({ ...prev, selectedTab: 'home' }))
      }
    },
    {
      key: 'documents',
      label: t('project.tabs.documents'),
      active: selectedTab === 'documents',
      onClick: () => {
        setProjects(prev => ({ ...prev, selectedTab: 'documents' }))
      }
    }
  ]

  const categories = useMemo(() => {
    if (!project) return []

    return (project.services || [])
      .reduce((acc, service) => {
        if (!service.category || service.category === 'legacy_project' || acc.some(v => v.name === service.category)) {
          return acc
        }

        const abandoned = (project.services || [])
          .filter(({ category }) => category === service.category)
          .every(service => service.state === 'abandoned')

        const color = abandoned
          ? theme.palette.error.main
          : theme.palette.primary.main

        return acc.findIndex(({ name }) => name === service.category) === -1
          ? [...acc, { name: service.category, color }]
          : acc
      }, [])
  }, [project, theme])

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" minWidth={0} gap={2}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Breadcrumbs>
          <Links links={tabs} onClick={(link) => link.onClick()} />
        </Breadcrumbs>
      </Box>

      <Stack
        direction="row"
        gap={{ xs: 1, lg: 2 }}
        alignItems="center"
        justifyContent="flex-end"
        sx={{
          flexShrink: 0,
          maxWidth: '58%',
          minWidth: 0,
          overflow: 'hidden'
        }}
      >
        {categories.map(category => (
          <Stack
            gap={1}
            alignItems="center"
            justifyContent="center"
            direction="row"
            key={category.name}
            sx={{ minWidth: 0, flexShrink: 1, pr: '12px' }}
          >
            <i className={`fa-light fa-${CATEGORIES_PROJECT[category.name]?.icon}`} style={{ color: category.color, flexShrink: 0, fontSize: 11 }} />
            <Typography
              component="span"
              sx={{
                fontSize: 11,
                lineHeight: '14px',
                color: theme.palette.grey[600],
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {t(`project.category.${category.name}`)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  )
}

const ProjectIdChip = ({ id }) => (
  <Box
    sx={{
      height: 36,
      px: 1.75,
      border: '1px solid #EFEFEF',
      borderRadius: '18px',
      display: 'flex',
      alignItems: 'center',
      color: '#464646',
      backgroundColor: '#fff',
      flexShrink: 0
    }}
  >
    <Typography component="span" sx={{ fontSize: 12, fontWeight: 400, color: '#464646', whiteSpace: 'nowrap' }}>
      {`Project #${id}`}
    </Typography>
  </Box>
)

const ProjectStatusChip = ({ status }) => {
  const { t } = useTranslation()
  const statusConfig = STATUS[status] || STATUS.standby

  return (
    <Box
      sx={{
        height: 20,
        px: 0.75,
        borderRadius: '999px',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        backgroundColor: statusConfig.bgcolor,
        color: statusConfig.color,
        flexShrink: 0
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: statusConfig.color,
          flexShrink: 0
        }}
      />
      <Typography component="span" sx={{ fontSize: 10, fontWeight: 500, lineHeight: '12px', color: statusConfig.color, whiteSpace: 'nowrap' }}>
        {t(`projects.table.status.${status}`)}
      </Typography>
    </Box>
  )
}

const DetailTitle = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [{ project }] = useContentProjects()
  const { id } = useParams()

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} minWidth={0} width="100%">
      <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
        <Box
          onClick={() => navigate('/projects')}
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            border: '1px solid #EFEFEF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#464646',
            flexShrink: 0
          }}
        >
          <i className="fa-light fa-arrow-left" style={{ fontSize: 12 }} />
        </Box>
        <Stack gap={0} minWidth={0}>
          <Typography sx={{ fontSize: 12, lineHeight: '12px', color: '#656565', fontWeight: 400 }}>
            {t(MENU_PROJECTS.label)}
          </Typography>
          <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
            <Typography
              sx={{
                color: '#2570EB',
                fontSize: 22,
                fontWeight: 300,
                lineHeight: '28px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {project?.site?.name || '-'}
            </Typography>
            {project?.state && <ProjectStatusChip status={project.state} />}
          </Stack>
        </Stack>
      </Stack>
      <ProjectIdChip id={id} />
    </Stack>
  )
}

const Menu = () => {
  const { id } = useParams()
  const { t } = useTranslation()

  return id ? (
    <HeaderBar title={t(MENU_PROJECTS.label)} titleContent={<DetailTitle />} hidePerimeters={true}>
      <Detail />
    </HeaderBar>
  ) : <HeaderBar title={t(MENU_PROJECTS.label)}><Home /></HeaderBar>
}

export default Menu